import { setNormalizedZoomLevel } from './canvas';
import logger from './controllers/logger';
import { getObjectsByType } from './entity';
import { v2 } from './helpers';
import { ObjectType } from './shared/api_shared_types';
import { GameManager, Time, type Canvas } from './types';

export let typedKeys: TypedKey[] = [];

//global
let scaling = false;
let startDist = 0;
let startNormalized = 0;
let canvasToZoom: Canvas = null;

type TypedKey = {
  key: string;
  code: number;
  time: Time;
  isDebugCommand: boolean;
  isPasteClipboard: boolean;
};

type ButtonState_t = {
  changedThisFrame: boolean;
  isDown: boolean;
  doRepeat: boolean;
  timeSinceLastPress: number; //time since key was pressed
  timeSinceLastRepeat: number; //time since last key repeat was fired
};

const resetButtonState = (doRepeat: boolean = false): ButtonState_t => {
  return {
    changedThisFrame: false,
    isDown: false,
    doRepeat: doRepeat,
    timeSinceLastPress: 0,
    timeSinceLastRepeat: 0,
  };
};

export const prevButtonStates = {
  //@mutates
  LEFT_MOUSE: resetButtonState(),
  TOUCH: resetButtonState(),
  ENTER: resetButtonState(),
  BACKSPACE: resetButtonState(true),
  ESCAPE: resetButtonState(true),
  SHIFT: resetButtonState(true),
  META: resetButtonState(true),
  CTR: resetButtonState(true),
  ALT: resetButtonState(true),
  LEFT: resetButtonState(true),
  RIGHT: resetButtonState(true),
  UP: resetButtonState(true),
  DOWN: resetButtonState(true),
};

export const buttonStates = {
  //@mutates
  LEFT_MOUSE: resetButtonState(),
  TOUCH: resetButtonState(),
  ENTER: resetButtonState(),
  BACKSPACE: resetButtonState(true),
  ESCAPE: resetButtonState(),
  SHIFT: resetButtonState(),
  META: resetButtonState(),
  CTRL: resetButtonState(),
  ALT: resetButtonState(),
  LEFT: resetButtonState(),
  RIGHT: resetButtonState(),
  UP: resetButtonState(),
  DOWN: resetButtonState(),
};

export function resetButtonStates() {
  buttonStates.LEFT.isDown = false;
  buttonStates.RIGHT.isDown = false;
  buttonStates.UP.isDown = false;
  buttonStates.DOWN.isDown = false;
}

export const clearTypedKeys = () => {
  typedKeys.length = 0;
};

export function setButtonState(buttonState: ButtonState_t, isDown: boolean) {
  buttonState.changedThisFrame = true;
  buttonState.isDown = isDown;
  buttonState.timeSinceLastPress = 0;
  buttonState.timeSinceLastRepeat = 0;
}

export function resetChangedThisFrameOnButtons(bs: any) {
  // @mutates
  Object.keys(bs).map((e) => {
    bs[e].changedThisFrame = false;
  });
}

export function onKeyDown(event: KeyboardEvent, time: Time) {
  const key = event.key;
  const keyCode = event.keyCode;

  if (key === 'Backspace' || keyCode === 8) {
    //backspace
    setButtonState(buttonStates.BACKSPACE, true);
  }

  if (key === 'Escape' || keyCode === 27) {
    //backspace
    setButtonState(buttonStates.ESCAPE, true);
  }

  if (key === 'Enter' || keyCode === 13) {
    //enter
    setButtonState(buttonStates.ENTER, true);
  }

  if (key === 'Shift' || keyCode === 16) {
    //shift
    setButtonState(buttonStates.SHIFT, true);
  }

  if (key === 'Meta' || keyCode === 92) {
    //shift
    setButtonState(buttonStates.META, true);
  }

  if (key === 'Control' || keyCode === 17) {
    //conrtol
    setButtonState(buttonStates.CTRL, true);
  }

  if (key === 'Alt' || keyCode === 18) {
    //conrtol
    setButtonState(buttonStates.ALT, true);
  }

  if (key === 'ArrowLeft' || keyCode === 37) {
    //conrtol
    setButtonState(buttonStates.LEFT, true);
  }
  if (key === 'ArrowUp' || keyCode === 38) {
    //conrtol
    setButtonState(buttonStates.UP, true);
  }
  if (key === 'ArrowRight' || keyCode === 39) {
    //conrtol
    setButtonState(buttonStates.RIGHT, true);
  }
  if (key === 'ArrowDown' || keyCode === 40) {
    //conrtol
    setButtonState(buttonStates.DOWN, true);
  }

  if (event.altKey && !buttonStates.ALT.isDown) {
    setButtonState(buttonStates.ALT, true);
  }
  if (!event.altKey && buttonStates.ALT.isDown) {
    setButtonState(buttonStates.ALT, false);
  }

  if (event.ctrlKey && !buttonStates.CTRL.isDown) {
    setButtonState(buttonStates.CTRL, true);
  }
  if (!event.ctrlKey && buttonStates.CTRL.isDown) {
    setButtonState(buttonStates.CTRL, false);
  }

  if (event.shiftKey && !buttonStates.SHIFT.isDown) {
    setButtonState(buttonStates.SHIFT, true);
  }
  if (!event.shiftKey && buttonStates.SHIFT.isDown) {
    setButtonState(buttonStates.SHIFT, false);
  }

  if (event.metaKey && !buttonStates.META.isDown) {
    setButtonState(buttonStates.META, true);
  }
  if (!event.metaKey && buttonStates.META.isDown) {
    setButtonState(buttonStates.META, false);
  }

  //Modify cursor text
  let isDebugCommand = buttonStates.SHIFT.isDown && buttonStates.CTRL.isDown;
  let isPasteClipboard = buttonStates.CTRL.isDown && event.key === 'v';
  if (isAllowedTyping(event.keyCode, event.key)) {
    typedKeys.push({
      key: event.key,
      code: event.keyCode,
      time,
      isDebugCommand,
      isPasteClipboard,
    });
  }
}

export function onKeyUp(event: KeyboardEvent) {
  const key = event.key;
  const keyCode = event.keyCode;

  if (key === 'Backspace' || keyCode === 8) {
    //backspace
    setButtonState(buttonStates.BACKSPACE, false);
  }

  if (key === 'Escape' || keyCode === 27) {
    //backspace
    setButtonState(buttonStates.ESCAPE, false);
  }

  if (key === 'Enter' || keyCode === 13) {
    //enter
    setButtonState(buttonStates.ENTER, false);
  }

  if (key === 'Shift' || keyCode === 16) {
    //shift
    setButtonState(buttonStates.SHIFT, false);
  }

  if (key === 'Meta' || keyCode === 92) {
    //shift
    setButtonState(buttonStates.META, false);
  }

  if (key === 'Control' || keyCode === 17) {
    //conrtol
    setButtonState(buttonStates.CTRL, false);
  }

  if (key === 'Alt' || keyCode === 18) {
    //conrtol
    setButtonState(buttonStates.ALT, false);
  }

  if (key === 'ArrowLeft' || keyCode === 37) {
    //conrtol
    setButtonState(buttonStates.LEFT, false);
  }

  if (key === 'ArrowUp' || keyCode === 38) {
    //conrtol
    setButtonState(buttonStates.UP, false);
  }

  if (key === 'ArrowRight' || keyCode === 39) {
    //conrtol
    setButtonState(buttonStates.RIGHT, false);
  }

  if (key === 'ArrowDown' || keyCode === 40) {
    //conrtol
    setButtonState(buttonStates.DOWN, false);
  }
}

function isAllowedTyping(keyCode: number, key: string): boolean {
  if (!key) return;

  const oneChar = key.length === 1;
  const notTagStart = key !== '<';
  // log({keyCode, key, oneChar, notTagStart});
  return oneChar && notTagStart;
}

export function isArrowKeyDown() {
  return (
    buttonStates.LEFT.isDown ||
    buttonStates.UP.isDown ||
    buttonStates.RIGHT.isDown ||
    buttonStates.DOWN.isDown
  );
}

export function isSpecialKeyDown() {
  return buttonStates.SHIFT.isDown || buttonStates.ALT.isDown || buttonStates.CTRL.isDown;
}

export function handleTouchStart(gameManager: GameManager, e: TouchEvent) {
  if (gameManager.paused) return;

  gameManager.cursor.enabled = false;

  if (e.changedTouches[0].identifier === 0) {
    gameManager.mousePos.x = e.changedTouches[0].clientX;
    gameManager.mousePos.y = e.changedTouches[0].clientY;
    setButtonState(buttonStates.LEFT_MOUSE, true);
  }

  if (e.touches.length === 2) {
    scaling = true;
    setButtonState(buttonStates.LEFT_MOUSE, false);

    startDist = Math.hypot(
      e.touches[0].pageX - e.touches[1].pageX,
      e.touches[0].pageY - e.touches[1].pageY,
    );

    canvasToZoom = null;

    //check if top highlighted entity is canvas
    if (gameManager.cursor.highlightTopEntity) {
      const id = gameManager.cursor.highlightTopEntity.id;

      const obj = gameManager.gameObjectIdTable[id];
      if (obj && obj.objectType === ObjectType.CANVAS) {
        if (obj.allowZoom) {
          canvasToZoom = obj;
        }
        // if (obj.allowScrollHor || obj.allowScrollVert) {
        //   canvasToScroll = obj;
        // }
      }
    }

    //if not, find the underlying canvas(es)
    if (!canvasToZoom) {
      const canvasses = Array.from(getObjectsByType<Canvas>(gameManager, ObjectType.CANVAS));
      for (let i = canvasses.length - 1; i >= 0; i--) {
        const canvasToCheck = canvasses[i];
        if (!canvasToCheck.allowHighlight) continue;
        if (canvasToCheck.mouseOver) {
          if (!canvasToZoom && e.ctrlKey && canvasToCheck.allowZoom) {
            canvasToZoom = canvasses[i];
          }
        }
      }
    }

    if (canvasToZoom) {
      startNormalized = canvasToZoom.normalizedZoomLevel;
    }
  }
  setButtonState(buttonStates.TOUCH, true);
}

export function handleTouchMove(gameManager: GameManager, e: TouchEvent) {
  if (e.changedTouches[0].identifier === 0) {
    gameManager.mousePos.x = e.changedTouches[0].clientX;
    gameManager.mousePos.y = e.changedTouches[0].clientY;
  }

  if (scaling) {
    const dist = Math.hypot(
      e.touches[0].pageX - e.touches[1].pageX,
      e.touches[0].pageY - e.touches[1].pageY,
    );
    const scale = dist / startDist;
    const zoomValue = (scale - 1) * 100 + startNormalized;
    const zoomCenter = v2(
      e.touches[0].pageX + (e.touches[1].pageX - e.touches[0].pageX) / 2,
      e.touches[0].pageY + (e.touches[1].pageY - e.touches[0].pageY) / 2,
    );

    if (canvasToZoom) {
      setNormalizedZoomLevel(canvasToZoom, zoomValue, zoomCenter);
    }
  }
  if (gameManager.preventScroll) {
    e.preventDefault();
  }

  // }
}

export function handleTouchEnd(gameManager: GameManager, e: TouchEvent) {
  if (gameManager.paused) return;
  if (e.changedTouches[0].identifier === 0) {
    gameManager.mousePos.x = e.changedTouches[0].clientX;
    gameManager.mousePos.y = e.changedTouches[0].clientY;
    setButtonState(buttonStates.LEFT_MOUSE, false);
  }

  if (e.touches.length < 2) {
    scaling = false;
  }

  setButtonState(buttonStates.TOUCH, false);
}
